import React, { useCallback, useState } from "react";
import { Category } from "@common/types/category";
import { Account } from "@common/types/domains/accounting/account";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { CategoryCombobox } from "../../transactions/TransactionSplitV2/CategoryCombobox";
import { Picker } from "@design-system/Picker/Picker.lib";
import { ClassCombobox } from "../../transactions/TransactionSplitV2/ClassCombobox";
import {
    CounterpartyCombobox,
    CounterpartyComboboxValue,
} from "../../transactions/TransactionSplitV2/CounterpartyCombobox";
import { BulkUpdateParams } from "../../transactions/TransactionsBulkActions/lib";
import { EntityUpdateButton } from "./SelectedTransactionsEntityUpdateButton";
import { FieldButtonProps } from "./SelectedTransactionsUpdateButtonTypes";
import { UpdateButtonContent } from "./SelectedTransactionsUpdateButtonCommon";
import { useSelectedTransactionsUpdateShortcut } from "./useSelectedTransactionsUpdateShortcut";
import styles from "./ActionsCard/ActionsCard.module.scss";

interface SelectedTransactionsFieldButtonProps extends FieldButtonProps {
    readonly update: BulkUpdateParams["update"];
}

const CategoryUpdateButton: React.FC<FieldButtonProps> = ({
    propertyLabel,
    selectedTransactions,
    applyValue,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleSelected = useCallback(
        (category: Category | undefined) => {
            if (!category) {
                return;
            }
            applyValue(category.id);
        },
        [applyValue],
    );

    useSelectedTransactionsUpdateShortcut({
        shortcut: "c",
        enabled: selectedTransactions.length > 0,
        propertyLabel,
        open: () => setIsOpen(true),
    });

    return (
        <CategoryCombobox
            onChange={handleSelected}
            suggestedCategories={[]}
            hideArrow
            open={isOpen}
            onOpenChange={setIsOpen}
            trigger={
                <Picker.ButtonTrigger
                    render={
                        <Picker.Button
                            variant="primary"
                            size="md"
                            className={styles.updateButton}
                        />
                    }
                    value={
                        <UpdateButtonContent
                            propertyLabel={propertyLabel}
                            shortcut="c"
                        />
                    }
                    showChevron={false}
                />
            }
        />
    );
};

const CounterpartyUpdateButton: React.FC<FieldButtonProps> = ({
    propertyLabel,
    selectedTransactions,
    applyValue,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleSelected = useCallback(
        (counterparty: CounterpartyComboboxValue | undefined) => {
            if (!counterparty) {
                return;
            }
            applyValue(counterparty.id, counterparty.name);
        },
        [applyValue],
    );

    useSelectedTransactionsUpdateShortcut({
        shortcut: "v",
        enabled: selectedTransactions.length > 0,
        propertyLabel,
        open: () => setIsOpen(true),
    });

    return (
        <CounterpartyCombobox
            onChange={handleSelected}
            forTransactionEditing
            hideArrow
            open={isOpen}
            onOpenChange={setIsOpen}
            trigger={
                <Picker.ButtonTrigger
                    render={
                        <Picker.Button
                            variant="primary"
                            size="md"
                            className={styles.updateButton}
                        />
                    }
                    value={
                        <UpdateButtonContent
                            propertyLabel={propertyLabel}
                            shortcut="v"
                        />
                    }
                    showChevron={false}
                />
            }
        />
    );
};

const CoaUpdateButton: React.FC<
    FieldButtonProps & { basis?: AccountingLedgerBasis }
> = ({ propertyLabel, selectedTransactions, applyValue, basis }) => {
    const [isOpen, setIsOpen] = useState(false);
    const firstTransaction = selectedTransactions[0];
    const shortcut = basis === AccountingLedgerBasis.accruals ? "a" : "g";

    useSelectedTransactionsUpdateShortcut({
        shortcut,
        enabled: Boolean(firstTransaction),
        propertyLabel,
        open: () => setIsOpen(true),
    });

    const handleAccountUpdate = useCallback(
        (account: Account | null) => {
            applyValue(account?.id ?? null, account?.name);
        },
        [applyValue],
    );

    if (!firstTransaction) {
        return null;
    }

    return (
        <AccountCombobox
            entityId={firstTransaction.entity.id}
            onChange={handleAccountUpdate}
            ledgerBasis={basis}
            addNewAccountButton={false}
            open={isOpen}
            onOpenChange={setIsOpen}
            trigger={
                <Picker.ButtonTrigger
                    render={
                        <Picker.Button
                            variant="primary"
                            size="md"
                            className={styles.updateButton}
                        />
                    }
                    value={
                        <UpdateButtonContent
                            propertyLabel={propertyLabel}
                            shortcut={shortcut}
                        />
                    }
                    showChevron={false}
                />
            }
        />
    );
};

const ClassesUpdateButton: React.FC<FieldButtonProps> = ({
    propertyLabel,
    selectedTransactions,
    applyValue,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [draftClassIds, setDraftClassIds] = useState<string[]>([]);

    useSelectedTransactionsUpdateShortcut({
        shortcut: "l",
        enabled: selectedTransactions.length > 0,
        propertyLabel,
        open: () => setIsOpen(true),
    });

    const handleChange = useCallback(
        (classIds: string[]) => {
            setDraftClassIds(classIds);
            applyValue(classIds);
        },
        [applyValue],
    );

    return (
        <ClassCombobox
            selectedClassIds={draftClassIds}
            onChange={handleChange}
            open={isOpen}
            onOpenChange={setIsOpen}
            trigger={
                <Picker.ButtonTrigger
                    render={
                        <Picker.Button
                            variant="primary"
                            size="md"
                            className={styles.updateButton}
                        />
                    }
                    value={
                        <UpdateButtonContent
                            propertyLabel={propertyLabel}
                            shortcut="l"
                        />
                    }
                    showChevron={false}
                />
            }
        />
    );
};

export const SelectedTransactionsFieldButton: React.FC<
    SelectedTransactionsFieldButtonProps
> = ({ update, propertyLabel, selectedTransactions, applyValue }) => {
    const props = {
        propertyLabel,
        selectedTransactions,
        applyValue,
    };

    switch (update) {
        case "categoryId":
            return <CategoryUpdateButton {...props} />;
        case "counterpartyId":
            return <CounterpartyUpdateButton {...props} />;
        case "coa":
            return <CoaUpdateButton {...props} />;
        case "accrualCoa":
            return (
                <CoaUpdateButton
                    {...props}
                    basis={AccountingLedgerBasis.accruals}
                />
            );
        case "classes":
            return <ClassesUpdateButton {...props} />;
        case "entity":
            return <EntityUpdateButton {...props} />;
        default:
            return null;
    }
};
